// Modo "Crear desde cero" — sheet del agente V2 inline.
//
// Tiene DOS CAMINOS, gobernados por el toggle "Ayuda de IA" (ADR-2026-08-19):
//
//   • DIRECTO (default, `aiHelp === false`): el prompt NO pasa por el LLM.
//     `handleGenerate` traduce el estado de la UI a una tool card y la empuja
//     a mano con `chat.pushToolCard` — mismo mecanismo scripted que el botón
//     "Sorpréndeme" de InterfazV2. Como NO se llama `pushUserMessage`, no hay
//     burbujas de chat: el panel queda como una tira de piezas generadas.
//     Sin aura. El toggle Imagen/Video decide qué tool se dispara.
//   • AYUDA DE IA (`aiHelp === true`): el agente conversacional de siempre,
//     con aura, y el toggle viajando como pista invisible vía `contextNote`.
//
// Y DOS ESTADOS VISUALES, ortogonales a lo anterior:
//
//   • Empty (sin mensajes): título "Crea sin límites" + cards de acción +
//     toggle + input. Es la "home" del modo.
//   • Active (después de la primera pieza o mensaje): el input se ancla al
//     pie del sheet y el área superior se vuelve el scroll de la sesión
//     (mensajes/tool cards). Los componentes de mensaje se importan de
//     ChatPanelV2 para no duplicar lógica.
//
// El cluster de blurs azules ("el aura") viaja por detrás de TODO el
// contenido en ambos estados, pero SOLO existe con Ayuda de IA prendida.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LoadingAnimation from '../../../components/LoadingAnimation';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Upload,
  Package,
  User,
  Image as ImageIcon,
  Smartphone,
  Layers,
  ArrowUp,
  MessageSquare,
  SquarePen,
  History,
  Coins,
  Brain,
  Trash2,
  X,
  Square,
  Sparkles,
} from 'lucide-react';
import { type UseV2ChatReturn, isInlinePendingCard } from '../useV2Chat';
import { listChats, type AgentChatSummary } from '../agentChatApi';
import {
  MessageRow,
  ThinkingIndicator,
  ToolCardRenderer,
  ProductFullMenu,
} from '../ChatPanelV2';
import {
  ModelPickerSheet,
  modelSelectionLabel,
  type ModelSelection,
} from '../ModelPicker';
import { useUserProducts } from '../productsContext';
import { useUserModels } from '../modelsContext';
import { useAgentEnv } from '../agentEnvContext';
import { useExitTransition } from '../../../hooks/useExitTransition';
import {
  uploadReferenceImage,
  signReferenceImage,
  type ChatAttachment,
} from '../agentAttachments';
import GeneratedImagesPicker, {
  type GeneratedPick,
} from '../GeneratedImagesPicker';
import { type Intelligence, intelligenceToModel } from '../agentModels';
import { OutputKindToggle } from './asistente/OutputKindToggle';
import { AccionCardsRow } from './asistente/AccionCardsRow';
import { AyudaIAMenu } from './asistente/AyudaIAMenu';
import { ConfiguracionMenu } from './asistente/ConfiguracionMenu';
import { ReferenciasSheet } from './asistente/ReferenciasSheet';
import {
  buildDirectToolCall,
  creditsForDirectCall,
  type OutputKind,
} from './asistente/directMode';
import { DEFAULT_FORMAT_ID } from '../mockData';
import {
  fetchMemories,
  deleteMemory,
  type AgentMemory,
} from '../agentMemoryApi';
import {
  EncabezadoPantalla,
  MARCO_PANTALLA,
  BORDE_PANTALLA,
} from '../../../components/PantallaChrome';
import { nombreCortoModo } from '../../estilos/EstiloMinimal';
// Gating de las dos opciones pagas del modo —
// ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
import {
  getOpcionAsistenteLock,
  puedeUsarAyudaIA,
  puedeGenerarVideoEnAsistente,
  type ModePlanLock,
} from '../../../lib/planCapabilities';
import { getUserPlan } from '../../../lib/planUtils';
import { FeatureLockedModal } from '../../../components/FeatureLockedModal';

export interface AsistenteMarketingSheetProps {
  animated: boolean;
  sidebarWidth: number;
  onClose: () => void;
  /** Instancia del chat del agente V2 — viene del padre (InterfazV2) para
   *  que se mantenga vivo aunque el sheet se cierre y reabra (el padre la
   *  resetea cuando corresponde). El sheet la consume vía MessageRow /
   *  ToolCardRenderer / sendMessage. */
  chat: UseV2ChatReturn;
}

export const AsistenteMarketingSheet: React.FC<AsistenteMarketingSheetProps> = ({
  animated,
  sidebarWidth,
  onClose,
  chat,
}) => {
  const [prompt, setPrompt] = useState<string>('');
  // Foco del textarea. En mobile, cuando el input toma foco (aparece el
  // teclado) en el empty-state, ocultamos sutilmente "Crea sin límites" y
  // mostramos las sugerencias sin su pill.
  const [inputFocused, setInputFocused] = useState<boolean>(false);
  // Cartelito "el asistente consume créditos" — el user lo puede descartar con
  // la x; lo persistimos en localStorage para no volver a mostrarlo.
  const [creditsDismissed, setCreditsDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('atlas-v2-credits-notice-dismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismissCreditsNotice = () => {
    setCreditsDismissed(true);
    try {
      localStorage.setItem('atlas-v2-credits-notice-dismissed', '1');
    } catch {
      /* noop */
    }
  };
  // Mobile: con el teclado abierto fijamos el sheet al área visible exacta que
  // reporta visualViewport (top = offsetTop, height = vv.height) en vez de
  // dejarlo `inset-0`. Así el input queda justo arriba del teclado y se compensa
  // el scroll que hace iOS del visual viewport (sin esto el contenido se corría
  // hacia arriba y el input quedaba cortado). Vacío (= inset-0) con teclado
  // cerrado / desktop. Lo recalcula el efecto de visualViewport.
  const [vvStyle, setVvStyle] = useState<React.CSSProperties>({});
  // Nivel de "inteligencia" (modelo). Persistido en localStorage; default
  // 'baja' (Gemini Flash — chatear no consume créditos, ADR-608). 'media'
  // usa Sonnet y 'alta' Opus. Se sincroniza con chat.setModel.
  const [intelligence, setIntelligence] = useState<Intelligence>(() => {
    try {
      const v = localStorage.getItem('atlas-v2-intelligence');
      if (v === 'baja' || v === 'media' || v === 'alta') return v;
    } catch {
      /* noop */
    }
    return 'baja';
  });
  const setModel = chat.setModel; // estable (useCallback) — evita re-runs
  useEffect(() => {
    setModel(intelligenceToModel(intelligence));
    try {
      localStorage.setItem('atlas-v2-intelligence', intelligence);
    } catch {
      /* noop */
    }
  }, [intelligence, setModel]);

  /* ── Plan, para gatear las dos opciones caras ───────────────────────────────
     ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

     El modo dejó de pedir Pro/Ultra para ENTRAR, así que ahora esta pantalla la abre
     cualquiera —incluida la prueba gratis— y es acá adentro donde hay que decidir qué
     puede prender. Este archivo no leía el plan porque hasta ahora no le hacía falta.

     Mismo arranque canónico que `/modos` y el drawer: el cache que ya escribe
     `useCuentaAtlas` para tener el candado desde el primer paint, y el valor real
     después. Con el plan desconocido NO se bloquea — mismo criterio que
     `getModePlanLock`, y el corte del submit queda de backstop. */
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);
  /** Lock de opción a mostrar (ayuda de IA o video), o `null`. */
  const [lockOpcion, setLockOpcion] = useState<ModePlanLock | null>(null);

  // ── Modo directo vs "Ayuda de IA" (ADR-2026-08-19) ─────────────────
  // Apagada = modo DIRECTO (el default): el prompt va al motor de
  // generación sin pasar por el agente, y el aura no se monta.
  //
  // El CLAMP va en el initializer y no sólo en el handler —
  // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones—: esta
  // clave es persistente, así que alguien que la prendió siendo Pro y después bajó de
  // plan (o que comparte el navegador con una cuenta Pro) rehidrataba la opción
  // prohibida y el candado no se enteraba nunca, porque el handler sólo corre cuando
  // se TOCA el control.
  //
  // Ojo con el orden: `plan` arranca del cache de localStorage, así que en el primer
  // render ya vale lo que corresponde. Si el cache está vacío, `puedeUsarAyudaIA('')`
  // da `false` y el clamp apaga la opción; cuando llegue el plan real, si es Pro el
  // efecto de más abajo NO la vuelve a prender —queda apagada, que es el lado seguro—
  // y el usuario la prende con un click.
  const [aiHelp, setAiHelp] = useState<boolean>(() => {
    try {
      if (localStorage.getItem('atlas-v2-ai-help') !== '1') return false;
      return puedeUsarAyudaIA(localStorage.getItem('atlas-user-plan'));
    } catch {
      return false;
    }
  });
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  // Qué genera el toggle de arriba del input. En directo decide la tool; con
  // Ayuda de IA viaja como pista al agente.
  // Mismo clamp persistente que `aiHelp`, por el mismo motivo.
  const [outputKind, setOutputKind] = useState<OutputKind>(() => {
    try {
      if (localStorage.getItem('atlas-v2-output-kind') !== 'video') return 'imagen';
      return puedeGenerarVideoEnAsistente(localStorage.getItem('atlas-user-plan'))
        ? 'video'
        : 'imagen';
    } catch {
      return 'imagen';
    }
  });

  /* Segundo clamp, cuando llega el plan REAL.
     El initializer corre contra el cache, que puede estar vacío o viejo. Si el plan que
     llega no alcanza, se apaga lo que haya quedado prendido. Sólo apaga: nunca prende
     nada por su cuenta. */
  useEffect(() => {
    if (!plan) return;
    if (!puedeUsarAyudaIA(plan)) setAiHelp(false);
    if (!puedeGenerarVideoEnAsistente(plan)) {
      setOutputKind((k) => (k === 'video' ? 'imagen' : k));
    }
  }, [plan]);

  /**
   * El ÚNICO camino para cambiar imagen↔video — ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
   *
   * El toggle se dibuja en CUATRO lugares (dos ramas del composer, el panel expandido
   * y `ConfiguracionMenu`), así que gatear en cada render era garantía de dejar un
   * bypass el día que aparezca el quinto. Se gatea el SETTER: los cuatro reciben esto y
   * `setOutputKind` crudo no sale de este bloque.
   */
  const cambiarOutputKind = useCallback(
    (k: OutputKind) => {
      if (k === 'video') {
        const lock = getOpcionAsistenteLock('video', plan);
        if (lock) {
          setLockOpcion(lock);
          return;
        }
      }
      setOutputKind(k);
    },
    [plan],
  );
  // Formato y duración: el motor los exige y en directo no hay agente que
  // los decida. `atlas-v2-last-format` es la MISMA clave que ya escribe la
  // tool card al generar, así que el default arranca en lo último usado.
  const [formatSlug, setFormatSlug] = useState<string>(() => {
    try {
      return localStorage.getItem('atlas-v2-last-format') || DEFAULT_FORMAT_ID;
    } catch {
      return DEFAULT_FORMAT_ID;
    }
  });
  const [videoDuration, setVideoDuration] = useState<number>(5);
  // El aura NO se monta/desmonta de golpe al cambiar de modo: se mantiene
  // montada durante el fade de salida y recién ahí se va. Así el cambio de
  // modo se siente sutil en vez de un corte seco. Cuando termina el fade sí
  // se desmonta, que es lo que corta las tres animaciones CSS infinitas.
  const { mounted: auraMounted, animated: auraVisible } = useExitTransition(
    aiHelp,
    700,
  );
  // El lead-in de 300ms del aura es SOLO para la entrada del sheet ("el blur
  // es lo último en encenderse"). Al togglear el modo estorba: el título ya
  // arrancó su fade a blanco y el aura todavía no salió, dejando un instante
  // de texto blanco sobre fondo claro. Este ref marca que la entrada ya pasó.
  const sheetEnteredRef = useRef(false);
  useEffect(() => {
    if (animated) sheetEnteredRef.current = true;
  }, [animated]);
  const auraDelayClass = sheetEnteredRef.current ? '' : 'delay-300';
  // Cuántas imágenes por tanda (solo aplica a Imagen). Vive en el menú de la
  // ruedita, como stepper +/-.
  const [cantidad, setCantidad] = useState<number>(1);
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  // Sheet de referencias (mobile) — la card única del empty state lo abre.
  const [referenciasOpen, setReferenciasOpen] = useState(false);

  // ── Modo DIRECTO: pantalla de carga tradicional ──────────────────────
  // Sin Ayuda de IA el modo se comporta como el resto de la app: overlay
  // full-screen mientras genera y, al terminar, salto a las imágenes
  // generadas. Con Ayuda de IA NO se usa: ahí la generación es un paso más
  // de la conversación y sacar al usuario del chat lo cortaría.
  const navigate = useNavigate();
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [completeProgressNow, setCompleteProgressNow] = useState(false);
  const pendingNavRef = useRef<{
    imageIds: (string | number)[];
    companyId: string;
  } | null>(null);

  // Costo del próximo envío en modo directo — se muestra en una pill al lado
  // del botón de generar.
  const directCreditCost = creditsForDirectCall({
    outputKind,
    cantidad,
    durationSeconds: videoDuration,
  });

  useEffect(() => {
    try {
      localStorage.setItem('atlas-v2-ai-help', aiHelp ? '1' : '0');
      localStorage.setItem('atlas-v2-output-kind', outputKind);
      localStorage.setItem('atlas-v2-last-format', formatSlug);
    } catch {
      /* noop */
    }
  }, [aiHelp, outputKind, formatSlug]);

  // La pista del toggle solo viaja si hay un agente escuchando.
  const setOutputKindHint = chat.setOutputKindHint; // estable (useCallback)
  useEffect(() => {
    setOutputKindHint(aiHelp ? outputKind : null);
  }, [aiHelp, outputKind, setOutputKindHint]);

  // Menú "+" de adjuntos — abierto/cerrado. Mismo state para mobile
  // (bottom sheet) y desktop (popup), el render distingue por
  // breakpoint.
  const [attachOpen, setAttachOpen] = useState<boolean>(false);
  // Vista interna del menú de adjuntos. `root` es la pantalla principal
  // (Subir imagen + Seleccionar producto / modelo / Otras fuentes).
  // `fuentes` es la sub-vista de "Otras fuentes" (Imagen ya generada,
  // Sacar foto, Cargar desde el móvil — stubs por ahora).
  const [attachView, setAttachView] = useState<'root' | 'fuentes'>('root');
  // Reset de la sub-vista al cerrar — si el user entró a `fuentes` y
  // cerró sin elegir nada, la próxima vez que abra el menú arranca de
  // vuelta en `root`.
  useEffect(() => {
    if (!attachOpen) setAttachView('root');
  }, [attachOpen]);
  // Cerrar el menú "+" al tocar/clickear afuera (desktop popup y, redundante
  // con el overlay, mobile sheet). El marcador `data-attach-menu` está tanto
  // en el ancla desktop como en el sheet mobile, así un click adentro no lo
  // cierra.
  useEffect(() => {
    if (!attachOpen) return;
    const onOutside = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('[data-attach-menu]')) return;
      setAttachOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
    };
  }, [attachOpen]);
  const closeAttach = () => setAttachOpen(false);

  // ── Adjuntos (menú "+") ─────────────────────────────────────────────
  // Picker abierto desde el menú "+": producto o modelo. La imagen usa un
  // <input type=file> oculto, no un picker.
  const [attachPicker, setAttachPicker] = useState<
    'none' | 'product' | 'model' | 'generated'
  >('none');
  const [imageUploading, setImageUploading] = useState(false);
  // Adjunto que se está viendo ampliado (lightbox). null = cerrado.
  const [previewAttachment, setPreviewAttachment] =
    useState<ChatAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  const { userId, companyId, refreshModels } = useAgentEnv();

  // Subir imagen de referencia: abre el file picker; al elegir, sube al
  // storage y agrega un chip de imagen.
  const onPickImageFile = async (file: File | null) => {
    if (!file || !companyId) return;
    setImageUploading(true);
    try {
      const { storagePath, previewUrl } = await uploadReferenceImage(
        file,
        companyId,
      );
      chat.addAttachment({
        kind: 'image',
        id: `img-${Date.now()}`,
        storagePath,
        previewUrl,
      });
    } catch (e) {
      console.error('[AsistenteMode] subir imagen falló', e);
    } finally {
      setImageUploading(false);
    }
  };

  // Aceptar productos del ProductFullMenu → chips de producto.
  const onAcceptProducts = (ids: string[]) => {
    for (const id of ids) {
      const p = userProducts.find((x) => x.id === id);
      if (!p) continue;
      chat.addAttachment({
        kind: 'product',
        id: `prod-${id}`,
        productId: id,
        name: p.name,
        imageUrl: p.imageUrl,
      });
    }
    setAttachPicker('none');
  };

  // Elegir una o varias imágenes ya generadas → chips de referencia (bucket
  // `generadas`).
  const onAcceptGenerated = (picks: GeneratedPick[]) => {
    picks.forEach((pick, i) => {
      chat.addAttachment({
        kind: 'image',
        id: `gen-${Date.now()}-${i}`,
        storagePath: pick.storagePath,
        previewUrl: pick.previewUrl,
        bucket: pick.bucket,
      });
    });
    setAttachPicker('none');
  };

  // Elegir modelo del ModelPickerSheet → chip de modelo (único).
  const onPickModel = (sel: ModelSelection) => {
    chat.addAttachment({
      kind: 'model',
      id: 'model-attachment',
      selection: sel,
      name: modelSelectionLabel(sel),
      imageUrl:
        sel.mode === 'preset'
          ? sel.imageUrl
          : sel.mode === 'upload'
            ? sel.previewUrl
            : null,
    });
    setAttachPicker('none');
  };

  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  // Ref al scroll de mensajes + al último mensaje del user (lo anclamos
  // arriba al enviar, estilo Director de Estudio).
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const latestUserMsgRef = useRef<HTMLDivElement | null>(null);
  // Mientras está "pineado", re-anclamos el último mensaje del user cerca
  // del tope en cada frame (así el contenido crece debajo y queda todo
  // visible). Se suelta cuando el user scrollea al fondo manualmente.
  const pinUserToTopRef = useRef<boolean>(false);

  // Token del run DIRECTO en vuelo. El tramo async de `handleGenerate` sobrevive
  // a cualquier cosa que pase mientras firma las imágenes adjuntas (una ida a la
  // red): si en ese hueco se prende Ayuda de IA o se empieza un chat nuevo, al
  // volver escribiría las referencias viejas y empujaría su tool card sobre una
  // sesión que ya es OTRA — trabajo del modo anterior metido en la nueva, y sus
  // referencias colgadas de los mensajes del agente que vengan después.
  //
  // Va acá y no con el `genToken` de `useV2Chat` porque ese sólo se bumpea en
  // `reset()`, y el caso que importa no resetea: con la sesión vacía
  // (`hasMessages === false`, o sea la primera generación directa) el cambio de
  // modo no pide confirmación ni limpia nada, sólo cambia de modo.
  const directRunRef = useRef(0);

  // ── Menú hamburguesa + historial + consumo ──────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  // Vista interna del menú hamburguesa: 'root' (3 opciones) o 'consumo'
  // (página de créditos gastados, dentro del mismo popover).
  const [menuView, setMenuView] = useState<'root' | 'consumo'>('root');
  // Memoria del agente — panel slide-in (mismo patrón que el historial).
  const [memoriaOpen, setMemoriaOpen] = useState(false);
  const [memoriasList, setMemoriasList] = useState<AgentMemory[]>([]);
  const [memoriasLoading, setMemoriasLoading] = useState(false);
  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialChats, setHistorialChats] = useState<AgentChatSummary[]>([]);
  const [historialLoading, setHistorialLoading] = useState(false);
  // Consumo del chat (créditos = agente LLM + imágenes), leído de la DB.
  const [consumoCredits, setConsumoCredits] = useState<number | null>(null);
  const [consumoLoading, setConsumoLoading] = useState(false);
  // Reset de la sub-vista al cerrar el menú.
  useEffect(() => {
    if (!menuOpen) setMenuView('root');
  }, [menuOpen]);
  // Al entrar a la página de consumo, leer el total real de la DB.
  useEffect(() => {
    if (menuView !== 'consumo') return;
    let alive = true;
    setConsumoLoading(true);
    chat.fetchConsumo().then((n) => {
      if (alive) {
        setConsumoCredits(n);
        setConsumoLoading(false);
      }
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuView]);
  const handleDeleteMemoria = async (id: string) => {
    setMemoriasList((prev) => prev.filter((m) => m.id !== id));
    await deleteMemory(id);
  };
  // Último chat (más reciente) — para el botón "Continuar chat anterior".
  const [lastChat, setLastChat] = useState<AgentChatSummary | null>(null);

  // Autosize del textarea (mismo patrón que el PromptBar de la home).
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [prompt]);

  // Ancla el último mensaje del user cerca del tope, con aire arriba.
  const scrollLatestUserToTop = (behavior: ScrollBehavior = 'smooth') => {
    const container = messagesScrollRef.current;
    const target = latestUserMsgRef.current;
    if (!container || !target) return;
    const cRect = container.getBoundingClientRect();
    const tRect = target.getBoundingClientRect();
    const offset = tRect.top - cRect.top + container.scrollTop - 16;
    container.scrollTo({ top: Math.max(0, offset), behavior });
  };

  // Auto-scroll. Si hay pin activo (turno en curso), mantenemos el mensaje
  // del user arriba. Sino, seguimos al fondo cuando el user ya estaba cerca.
  useEffect(() => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    if (pinUserToTopRef.current) {
      scrollLatestUserToTop('smooth');
      return;
    }
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 120) {
      sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
    }
  }, [chat.messages, chat.activeToolCard, chat.streaming]);

  // Loop que re-ancla el mensaje del user arriba mientras el agente trabaja
  // (texto en streaming, cards que aparecen, etc. empujan contenido y
  // pueden moverlo). Se corta al terminar el turno o al soltar el pin.
  useEffect(() => {
    if (!chat.streaming || !pinUserToTopRef.current) return;
    let raf = 0;
    const tick = () => {
      if (!pinUserToTopRef.current) return;
      const container = messagesScrollRef.current;
      const target = latestUserMsgRef.current;
      if (container && target) {
        const cRect = container.getBoundingClientRect();
        const tRect = target.getBoundingClientRect();
        const offset = tRect.top - cRect.top + container.scrollTop - 16;
        const clamped = Math.max(0, offset);
        if (Math.abs(container.scrollTop - clamped) > 1) {
          container.scrollTop = clamped;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [chat.streaming]);

  // Si el user scrollea hasta el fondo, soltamos el pin (quiere seguir el
  // contenido nuevo, no quedarse anclado arriba).
  const handleMessagesScroll = () => {
    const sc = messagesScrollRef.current;
    if (!sc) return;
    const dist = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (dist < 60) pinUserToTopRef.current = false;
  };

  // Soltar el pin ante CUALQUIER scroll manual (touch/wheel), no solo al llegar
  // al fondo. Sin esto, mientras el agente streamea, el RAF de arriba re-fija el
  // scroll cada frame y el usuario NO puede scrollear (rebota a la posición
  // pineada) — y como nunca puede llegar al fondo, la única vía de soltar el pin
  // (`dist < 60`) es inalcanzable. Apenas el user toma control con un gesto, el
  // pin se suelta y el RAF para (chequea pinUserToTopRef.current al inicio del tick).
  const releasePinOnUserScroll = () => {
    pinUserToTopRef.current = false;
  };

  const hasText = prompt.trim().length > 0;
  const hasMessages = chat.messages.length > 0;
  // Agente ya conversando: con Ayuda de IA prendida Y conversación empezada.
  // A partir de acá los controles de generación (ruedita + toggle
  // Imagen/Video) se esconden — quien decide formato, cantidad, duración y
  // si sale imagen o video es el agente, según lo que le pidas por texto.
  const agenteConversando = aiHelp && hasMessages;
  // Mientras el agente está respondiendo o pidiendo una tool, no aceptamos
  // input nuevo — replica el comportamiento del ChatPanelV2.
  // Se puede escribir mientras una PROPUESTA espera: al enviar, la propuesta
  // no se pierde — queda en el chat y se sigue pudiendo generar (detachTurn).
  // Lo que sigue bloqueado es enviar mientras el agente está redactando.
  const sendDisabled = chat.streaming && !chat.activeToolCard;
  // Índice del último mensaje del user — para anclarlo arriba.
  const lastUserIdx = (() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === 'user') return i;
    }
    return -1;
  })();

  const handleGenerate = () => {
    if (sendDisabled) return;
    const trimmed = prompt.trim();
    if (!trimmed) return;

    /* ── Backstop de plan ──────────────────────────────────────────────────────
       ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

       Los candados de arriba son de UI, y en este modo la UI es lo ÚNICO que hay:
       `checkGenerationGuards('agente')` existe en `InterfazV2` pero NINGÚN call site lo
       usa (los dos que hay pasan 'catalogo' o 'video'), o sea que el camino de
       generación del asistente —directo y agente— nunca consultó el plan. Hasta ahora
       no se notaba porque el modo entero estaba bloqueado; al abrirlo, sin esto el
       único freno sería el 402 del backend, o sea después de gastar el viaje.

       Corta LOS DOS caminos, y por eso va antes del `if (aiHelp)`. */
    if (aiHelp) {
      const lock = getOpcionAsistenteLock('ayuda-ia', plan);
      if (lock) {
        setAiHelp(false);
        setLockOpcion(lock);
        return;
      }
    }
    if (outputKind === 'video') {
      const lock = getOpcionAsistenteLock('video', plan);
      if (lock) {
        setOutputKind('imagen');
        setLockOpcion(lock);
        return;
      }
    }

    if (aiHelp) {
      // ── Camino con AGENTE ─────────────────────────────────────────
      // Si hay una propuesta esperando, soltamos ese turno ANTES de mandar:
      // sin esto `sendMessage` se descartaba en silencio (su guard mira
      // `streaming`, que sigue en true mientras la card espera). La propuesta
      // NO se cancela — queda en el chat, y se puede volver y generarla.
      if (chat.activeToolCard) chat.detachTurn();
      chat.sendMessage(trimmed);
      setPrompt('');
      // Anclar el nuevo mensaje del user arriba, con aire para la respuesta.
      pinUserToTopRef.current = true;
      requestAnimationFrame(() => scrollLatestUserToTop('smooth'));
      return;
    }

    // ── Camino DIRECTO (ADR-2026-08-19) ─────────────────────────────
    // Sin LLM: se empuja la tool card a mano, mismo mecanismo scripted
    // que "Sorpréndeme" (InterfazV2). NO se llama pushUserMessage, así
    // que no hay burbuja de usuario ni texto de Atlas: el panel queda
    // como una tira de piezas generadas.
    const atts = chat.attachments;
    const { kind, input } = buildDirectToolCall({
      outputKind,
      prompt: trimmed,
      formatSlug,
      cantidad,
      durationSeconds: videoDuration,
      attachments: atts,
    });
    setPrompt('');

    const runToken = ++directRunRef.current;
    /** ¿Este run sigue siendo el vigente? Ver `directRunRef`. */
    const vigente = () => runToken === directRunRef.current;

    void (async () => {
      try {
        // Las imágenes adjuntas viajan como {source:'adjunto'}, que la card
        // resuelve leyendo getReferenceImages(). Ese ref lo puebla
        // sendMessage, que acá no corre — así que las firmamos nosotros.
        const imageAtts = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'image' }> =>
            a.kind === 'image',
        );
        const urls =
          imageAtts.length > 0
            ? await Promise.all(
                imageAtts.map((a) => signReferenceImage(a.storagePath, a.bucket)),
              )
            : [];
        // Firmar es una ida a la red y el usuario pudo cambiar de modo o
        // arrancar de cero mientras tanto. Se corta ANTES de la primera
        // escritura: a partir de acá todo lo que sigue toca la sesión.
        if (!vigente()) return;
        chat.setReferenceImages(urls.filter((u): u is string => !!u));
        chat.clearAttachments();
        // La IMAGEN autogenera, así que el overlay arranca ya. El VIDEO no:
        // primero muestra su card de confirmación con el costo, y ahí el
        // overlay taparía justo lo que hay que confirmar.
        const conOverlay = outputKind === 'imagen';
        if (conOverlay) {
          setCompleteProgressNow(false);
          setShowLoadingScreen(true);
        }
        const res: any = await chat.pushToolCard(kind, input);
        // Segundo corte: generar tarda, y el cambio de modo con la sesión vacía
        // no cancela la card (no hay `reset()` que bumpee el genToken del hook).
        // Sin esto, al terminar se navegaría a generadas y se resetearía la
        // sesión del agente en la que el usuario ya está trabajando. Las piezas
        // no se pierden: quedan en el historial de imágenes generadas.
        if (!vigente()) return;
        const ids: (string | number)[] = res?.image_ids ?? [];
        if (conOverlay) {
          if (ids.length > 0) {
            pendingNavRef.current = {
              imageIds: ids,
              companyId: res?.company_id || companyId || '',
            };
            // El resultado ya vive en la pantalla de generadas, así que la
            // card que quedó en el stream es puro residuo: en modo directo
            // no hay conversación que la contextualice. Limpiamos para que
            // al volver al modo esté en blanco. (Solo con éxito: si algo
            // falló, la card se queda para mostrar el error.)
            chat.reset();
          }
          // Cierra la barra donde esté: `onCompleted` navega.
          setCompleteProgressNow(true);
        }
      } catch (err) {
        console.error('[AsistenteMode] modo directo falló', err);
        // El overlay se baja SOLO si este run sigue siendo el vigente: si ya
        // arrancó otro, bajarlo le apagaría la pantalla de carga al run nuevo.
        if (!vigente()) return;
        // Si el overlay quedó arriba, lo bajamos: sin esto la pantalla de
        // carga se queda para siempre sobre un error que nadie ve.
        setShowLoadingScreen(false);
        setCompleteProgressNow(false);
      }
    })();
  };

  // Cambiar de modo con piezas en pantalla limpia la sesión: lo generado en
  // directo no está en el historial de API del agente, así que dejarlo a la
  // vista produciría un agente que "no ve" lo que el usuario tiene delante.
  // Las piezas no se pierden — siguen en el historial de imágenes generadas.
  const handleToggleAiHelp = (on: boolean) => {
    // Candado de plan ANTES del confirm —
    // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
    // Va primero a propósito: preguntarle "¿empezamos una sesión nueva?" a alguien que
    // no va a poder prender la opción es pedirle que confirme algo que no va a pasar.
    // Sólo se gatea PRENDER: apagarla siempre se puede.
    if (on) {
      const lock = getOpcionAsistenteLock('ayuda-ia', plan);
      if (lock) {
        setLockOpcion(lock);
        return;
      }
    }
    if (hasMessages) {
      const ok = window.confirm(
        on
          ? '¿Activar la Ayuda de IA? Se empieza una sesión nueva. Lo que generaste queda en tu historial.'
          : '¿Desactivar la Ayuda de IA? Se empieza una sesión nueva. Lo que generaste queda en tu historial.',
      );
      if (!ok) return;
      chat.reset();
    }
    // Recién acá, con el cambio ya confirmado: invalida cualquier generación
    // directa en vuelo, porque la sesión que estaba llenando deja de ser la de
    // esta pantalla. Si el usuario canceló el confirm, arriba ya se volvió y el
    // run sigue vivo, que es lo correcto. Ver `directRunRef`.
    directRunRef.current += 1;
    // El menú NO se cierra a propósito: el cambio de modo es sutil (el aura
    // hace fade, el título transiciona de color) y cerrar el menú de golpe
    // rompía esa sensación — además de esconder el switch que se acaba de
    // tocar.
    setAiHelp(on);
  };

  // Traer el chat más reciente para el botón "Continuar chat anterior" —
  // solo cuando el chat actual está vacío (sino ya estás en uno).
  useEffect(() => {
    if (hasMessages) {
      setLastChat(null);
      return;
    }
    let alive = true;
    (async () => {
      const chats = await listChats();
      if (alive) setLastChat(chats[0] ?? null);
    })();
    return () => {
      alive = false;
    };
  }, [hasMessages]);

  // Mobile: con el teclado abierto fijamos el sheet al área visible EXACTA que
  // reporta visualViewport (top = offsetTop + height = vv.height) en vez de
  // inset-0. Así el input (al pie del flex) queda justo arriba del teclado y NO
  // se corre cuando iOS scrollea el visual viewport para mostrarlo.
  //
  // OJO: NO usamos `window.innerHeight` como base para detectar el teclado — en
  // iOS es mayor que `visualViewport.height` aun SIN teclado (home indicator /
  // toolbar). La base es el MAYOR `vv.height` observado (= viewport sin teclado);
  // si vv.height baja respecto de esa base, hay teclado abierto.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let lastWidth = 0;
    const apply = () => {
      if (window.innerWidth >= 768) {
        setVvStyle({});
        return;
      }
      // Reset de la base al cambiar el ancho (rotación), sino queda stale.
      if (lastWidth !== window.innerWidth) {
        lastWidth = window.innerWidth;
        baseline = vv.height;
      }
      if (vv.height > baseline) baseline = vv.height;
      const kb = Math.max(0, baseline - vv.height);
      // Con el teclado abierto, iOS no solo ENCOGE el visual viewport: también
      // lo SCROLLEA (offsetTop > 0) para meter el input en vista. El sheet es
      // `fixed inset-0` (anclado al layout viewport), así que sin compensar ese
      // offset el contenido se corre hacia arriba — el input queda cortado y
      // aparece una franja vacía entre él y el teclado. Lo fijamos EXACTO al
      // área visible: top = offsetTop, height = vv.height (en Android offsetTop
      // es 0, así que queda top:0 + height = área sobre el teclado). Sin teclado
      // (kb≈0) volvemos a inset-0 (vvStyle vacío).
      setVvStyle(
        kb > 1
          ? {
              top: `${Math.max(0, vv.offsetTop)}px`,
              height: `${vv.height}px`,
              bottom: 'auto',
            }
          : {},
      );
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('resize', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('resize', apply);
    };
  }, []);

  const openHistorial = async () => {
    setMenuOpen(false);
    setHistorialOpen(true);
    setHistorialLoading(true);
    const chats = await listChats();
    setHistorialChats(chats);
    setHistorialLoading(false);
  };

  const openMemoria = async () => {
    setMenuOpen(false);
    setMemoriaOpen(true);
    if (!companyId) {
      setMemoriasList([]);
      return;
    }
    setMemoriasLoading(true);
    const ms = await fetchMemories(companyId);
    setMemoriasList(ms);
    setMemoriasLoading(false);
  };

  const handleNewChat = () => {
    setMenuOpen(false);
    // Misma razón que en el cambio de modo: una generación directa en vuelo
    // no tiene que aterrizar sobre el chat nuevo. Ver `directRunRef`.
    directRunRef.current += 1;
    chat.reset();
  };

  // Fecha relativa cortita para el historial.
  const relativeDate = (iso: string): string => {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'recién';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h} h`;
    const days = Math.floor(h / 24);
    if (days < 7) return `hace ${days} d`;
    return d.toLocaleDateString();
  };

  // Contenido del menú "+" — JSX compartido entre el popup desktop y el
  // bottom sheet mobile. Tiene 2 sub-vistas internas (state `attachView`):
  //   • root    — Subir imagen (botón primario), Seleccionar producto /
  //               modelo, Otras fuentes (entra a la sub-vista).
  //   • fuentes — Volver + Imagen ya generada / Sacar foto / Cargar
  //               desde el móvil (los 3 son stubs por ahora).
  const attachMenuContent =
    attachView === 'root' ? (
      <div className="flex flex-col gap-1">
        {/* Subir imagen — CTA primario con bg brand-primary, sin chevron.
            Es la acción más común así que rompe el patrón de fila gris. */}
        <button
          type="button"
          onClick={() => {
            closeAttach();
            fileInputRef.current?.click();
          }}
          className="w-full inline-flex items-center justify-center gap-2 py-3 mb-1 rounded-xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          <Upload className="w-4 h-4" />
          Añadir imagen
        </button>
        {/* Las demás opciones (no-imagen): en mobile van agrupadas en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y las filas fluyen como antes en el popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<Package className="w-4 h-4" />}
            label="Añadir producto"
            onClick={() => {
              closeAttach();
              setAttachPicker('product');
            }}
          />
          <AttachMenuItem
            icon={<User className="w-4 h-4" />}
            label="Añadir modelo"
            onClick={() => {
              closeAttach();
              setAttachPicker('model');
            }}
          />
          <AttachMenuItem
            icon={<Layers className="w-4 h-4" />}
            label="Añadir desde otras fuentes"
            onClick={() => setAttachView('fuentes')}
          />
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        {/* Header "Volver" — link azul a la izquierda para regresar al
            root del menú sin cerrar el popup/sheet. */}
        <button
          type="button"
          onClick={() => setAttachView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        {/* Mismo tratamiento que el root: en mobile las opciones van en un
            container tipo lista con divisores; en desktop (`md:contents`) el
            wrapper desaparece y fluyen como filas del popup. */}
        <div className="mt-1 rounded-2xl border border-black/[0.06] divide-y divide-black/[0.05] overflow-hidden md:mt-0 md:contents md:rounded-none md:border-0 md:divide-y-0">
          <AttachMenuItem
            icon={<ImageIcon className="w-4 h-4" />}
            label="Añadir imagen ya generada"
            onClick={() => {
              closeAttach();
              setAttachPicker('generated');
            }}
          />
          {/* "Añadir desde el móvil" — solo en escritorio (en mobile ya estás
              en el teléfono, no tiene sentido). */}
          <div className="hidden md:block">
            <AttachMenuItem
              icon={<Smartphone className="w-4 h-4" />}
              label="Añadir desde el móvil"
              onClick={closeAttach}
            />
          </div>
        </div>
      </div>
    );

  // Botón "Continuar con el chat anterior" (aparece en empty state si hay
  // un chat previo en la DB).
  const continueButton = lastChat ? (
    <button
      type="button"
      onClick={() => chat.loadChatById(lastChat.id)}
      className="w-full max-w-[680px] inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border border-black/[0.08] hover:border-brand-primary/40 text-[14px] font-medium text-brand-black/85 shadow-sm transition-colors"
    >
      <History className="w-4 h-4 text-brand-primary shrink-0" />
      <span className="truncate">Continuar con el chat anterior</span>
    </button>
  ) : null;

  // Contenido del menú hamburguesa. 'root' = 3 opciones; 'consumo' = página
  // de créditos gastados (dentro del mismo popover, con botón Volver).
  const menuContent =
    menuView === 'consumo' ? (
      <div className="flex flex-col">
        <button
          type="button"
          onClick={() => setMenuView('root')}
          className="self-start inline-flex items-center gap-0.5 px-2 py-2 text-[13.5px] text-brand-primary hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="px-3 pb-3 pt-1">
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
            Consumo de este chat
          </p>
          <div className="flex items-center gap-2 mt-2">
            <img
              src="/images/token_icon.png"
              alt=""
              aria-hidden="true"
              className="w-6 h-6 object-contain"
            />
            <span className="text-[26px] font-bold text-brand-primary leading-none">
              {consumoLoading ? '…' : (consumoCredits ?? 0)}
            </span>
            <span className="text-[13px] font-medium text-neutral-500">
              {(consumoCredits ?? 0) === 1 ? 'crédito' : 'créditos'}
            </span>
          </div>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-1.5 leading-snug">
            Incluye el costo del asistente y las imágenes generadas en esta
            conversación.
          </p>
        </div>
      </div>
    ) : (
      <div className="flex flex-col gap-1">
        <AttachMenuItem
          icon={<Plus className="w-4 h-4" />}
          label="Nuevo chat"
          onClick={handleNewChat}
        />
        <AttachMenuItem
          icon={<Coins className="w-4 h-4" />}
          label="Consumo"
          onClick={() => setMenuView('consumo')}
        />
        <AttachMenuItem
          icon={<Brain className="w-4 h-4" />}
          label="Memoria"
          onClick={openMemoria}
        />
        <AttachMenuItem
          icon={<History className="w-4 h-4" />}
          label="Historial"
          onClick={openHistorial}
        />
      </div>
    );

  // Lista del historial (loading / vacío / items).
  const historialListContent = historialLoading ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">Cargando…</p>
  ) : historialChats.length === 0 ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">
      Todavía no hay chats.
    </p>
  ) : (
    <div className="flex flex-col gap-0.5">
      {historialChats.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => {
            setHistorialOpen(false);
            chat.loadChatById(c.id);
          }}
          className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
        >
          <p className="text-[14.5px] font-medium text-brand-black/90 truncate">
            {c.title || 'Chat'}
          </p>
          <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">
            {relativeDate(c.updated_at)}
          </p>
        </button>
      ))}
    </div>
  );

  // Lista de memorias (loading / vacío / items con borrar).
  const memoriaListContent = memoriasLoading ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8">Cargando…</p>
  ) : memoriasList.length === 0 ? (
    <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8 px-4 leading-snug">
      Todavía no hay nada guardado. El asistente va a recordar tus preferencias
      y datos de tu marca a medida que charlen.
    </p>
  ) : (
    <div className="flex flex-col gap-1">
      {memoriasList.map((m) => (
        <div
          key={m.id}
          className="group flex items-start gap-2 px-3 py-2.5 rounded-xl hover:bg-neutral-50 transition-colors"
        >
          <span className="flex-1 min-w-0 text-[14px] text-brand-black/90 leading-snug">
            {m.content}
          </span>
          <button
            type="button"
            onClick={() => handleDeleteMemoria(m.id)}
            aria-label="Borrar memoria"
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );

  // Portal a `document.body` para escapar de cualquier stacking context
  // creado por ancestros de InterfazV2 (transform/filter/etc.). Sin esto
  // el `z-[201]` queda atrapado en el árbol y el chrome del Sidebar
  // mobile (z-50) termina pintando por encima del sheet.
  return ReactDOM.createPortal(
    <>
      {/* Backdrop — en mobile invisible (el sheet ocupa toda la pantalla);
          en desktop arranca después del sidebar para no oscurecerlo. El
          sidebar queda completamente clickeable y con su look normal.
          z-30 queda debajo del chrome mobile (z-50) para que pills back /
          créditos / menú sigan visibles arriba. */}
      <div
        className={`fixed inset-0 md:left-[var(--sidebar-w,0px)] z-30 bg-black/0 md:bg-black/30 md:rounded-tl-[26px] md:rounded-bl-[26px] ${MARCO_PANTALLA} transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        style={
          { '--sidebar-w': `${sidebarWidth}px` } as React.CSSProperties
        }
        onClick={onClose}
      />
      {/* Sheet container.
          • Mobile  → fixed inset-0, slide-up desde abajo. z-40 mantiene
            el sheet POR DEBAJO del chrome (z-50) del Sidebar para que el
            user siga viendo los pills de tokens / menú arriba.
          • Desktop → fixed con left=sidebarWidth, slide desde la derecha
            (mismo patrón que PresetDetailSheet). */}
      <div
        className={`fixed inset-0 z-40 bg-surface-mobile md:bg-surface-content flex flex-col transition-[transform,opacity] duration-300 ease-out md:left-[var(--sidebar-w,0px)] md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${MARCO_PANTALLA} ${BORDE_PANTALLA} ${
          animated
            ? 'translate-y-0 opacity-100'
            : // Mobile → slide-up desde abajo (sheet). Desktop → entrada
              // sutil estilo `mode-swap`: fade + 4px de translateY (mismo
              // lenguaje que el resto de los modos), sin slide horizontal.
              'translate-y-full opacity-100 md:translate-y-1 md:opacity-0'
        }`}
        style={
          // CSS var con el ancho del sidebar — sólo aplica en md+ vía la
          // clase `md:left-[var(--sidebar-w)]` de arriba. En mobile vvStyle
          // suma un padding-bottom = alto del teclado (el sheet sigue
          // inset-0); en md+ vvStyle está vacío.
          {
            '--sidebar-w': `${sidebarWidth}px`,
            ...vvStyle,
          } as React.CSSProperties
        }
      >
        {/* Header — solo visible en desktop con "Volver" texto (mismo
            patrón que el PresetDetailSheet). En mobile el chrome del
            Sidebar ya tiene su back chevron pill (vía mobileBackOverride
            seteado por el useEffect del padre), así que no duplicamos el
            botón acá. */}
        {/* Mismo encabezado que las otras pantallas de modo
            (ADR-2026-08-03-chrome-de-pantalla-compartido). Este modo tiene UNA sola
            pantalla —no hay pasos que encadenar—, así que son dos niveles y el
            asistente es la hoja. `hidden md:flex` lo sigue escondiendo en mobile,
            donde el chrome del Sidebar ya trae su propio back.

            El nombre sale de `nombreCortoModo` (o sea de `COPY_TIRA`) y no de una
            constante acá: es la misma tabla del carrusel de la home, la sidebar y
            el sheet de Generar, así que el modo se llama igual en las cuatro
            superficies — ADR-2026-08-03-un-solo-nombre-por-modo. */}
        <EncabezadoPantalla
          className="hidden md:flex items-center justify-between md:px-8 md:pt-6"
          migas={[
            { label: 'Inicio', onClick: onClose },
            { label: nombreCortoModo('asistente', 'Asistente') },
          ]}
        >
          {/* Nuevo chat — DESKTOP, pill arriba a la derecha. Va como `children` del
              encabezado compartido, que reserva ese lugar para las acciones de cada
              pantalla. Solo con algo en pantalla: en el empty state no hay nada que
              limpiar. Sirve en los dos modos (en directo era la única forma de
              empezar de cero que había quedado sin puerta). */}
          {hasMessages && (
            <button
              type="button"
              onClick={handleNewChat}
              className="inline-flex items-center gap-1.5 py-2 px-4 rounded-full bg-white border border-black/[0.06] shadow-sm text-[13.5px] font-medium text-[#02062BE6] hover:border-brand-primary/30 transition-colors"
            >
              <SquarePen className="w-4 h-4" strokeWidth={2.25} />
              Nuevo chat
            </button>
          )}
        </EncabezadoPantalla>

        {/* Nuevo chat — MOBILE, pegado a la derecha del back del chrome del
            Sidebar. Las medidas replican ese chrome para quedar alineado:
            fila de h-14 con px-4 y un pill de w-12, o sea que el back ocupa
            16→64px y este arranca en 72px (4.5rem). El top centra los 48px
            del botón en los 56px de la fila: (56−48)/2 = 4px. */}
        {hasMessages && (
          <button
            type="button"
            onClick={handleNewChat}
            aria-label="Nuevo chat"
            className="md:hidden absolute left-[4.5rem] z-50 w-12 h-12 rounded-full bg-white/95 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] text-brand-black/85 inline-flex items-center justify-center active:opacity-50 transition-all"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.25rem)' }}
          >
            <SquarePen size={22} strokeWidth={2} />
          </button>
        )}

        {/* Contenido principal. Layout flex-col que se reparte entre
            zona scrolleable (título/empty O mensajes/active) arriba y
            zona fija (toggle + input, o la tool card) abajo. En mobile
            sumamos `safe-area-inset-top + 3.5rem` (alto del chrome del
            Sidebar) para que el primer elemento no quede debajo del
            back pill / créditos / menú. */}
        <div className="relative flex-1 min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top,0px)+3.5rem)] md:pt-0">
          {/* Cluster de blobs azules de fondo — vive a nivel del sheet,
              detrás de TODO el contenido (z-0). Igual en empty y active
              states: es aura ambiente, no parte del header de empty.

              Las CSS variables `--drift-N-dur` controlan el tiempo de
              cada animación drift, overrideando el default de
              `animate-drift-N` (4-5s). En mobile mantenemos el ritmo
              original; en desktop bajamos a ~14-16s para un drift muy
              lento, más calmo y menos competitivo con el texto/input.

              Solo existe con "Ayuda de IA" prendida: el modo directo es
              sobrio. Se DESMONTA en vez de atenuarse, así se cortan las
              tres animaciones CSS infinitas en el camino que ahora es el
              default (ADR-2026-08-19). */}
          {auraMounted && (
          <div
            aria-hidden="true"
            // 3 estados de movimiento + opacidad:
            //   • Empty                         → centrado, opacidad full
            //   • Active idle / esperando user  → bajado al pie (sale de
            //     pantalla), opacidad bien baja para no competir con la
            //     tool card o el input cuando el user tiene que actuar.
            //   • Active streaming (agente pensando / respondiendo) →
            //     misma posición que idle pero opacidad sube sutilmente,
            //     como un "respiro" del aura mientras Atlas trabaja.
            //
            // El transform se desliza rápido (250ms) para acompañar el
            // "snap" del input que cambia de rama JSX (empty → active) y
            // no se siente desfasado del container. La opacidad tiene
            // su propio timing más lento (~900ms) — el "respiro" del
            // streaming debe ser sutil, no abrupto.
            className={`pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-visible [--drift-1-dur:4s] [--drift-2-dur:5s] [--drift-3-dur:4.5s] md:[--drift-1-dur:14s] md:[--drift-2-dur:16s] md:[--drift-3-dur:15s] ${
              hasMessages
                ? `translate-y-[65%] md:translate-y-[60%] ${
                    chat.streaming ? 'opacity-60' : 'opacity-25'
                  }`
                : inputFocused
                  ? // Mobile: al enfocar el input (teclado), el aura se baja
                    // sutilmente al estado "active idle" anticipando el chat.
                    // Desktop: se mantiene centrado y full (no hay teclado).
                    'translate-y-[65%] opacity-25 md:translate-y-0 md:opacity-100'
                  : 'translate-y-0 opacity-100'
            }`}
            style={{
              transition:
                'transform 250ms ease-out, opacity 900ms ease-out',
            }}
          >
            {/* Doble condición de opacidad:
                • `animated`   — entrada del SHEET. El blur es lo último en
                  aparecer: mientras el sheet hace su fade/slide (~300ms) el
                  aura queda en 0 y recién ahí arranca su fade-in.
                • `auraVisible` — cambio de MODO (Ayuda de IA on/off), vía
                  useExitTransition. Acá NO va el delay (ver auraDelayClass):
                  el aura tiene que salir junto con el cambio de color del
                  título, si no se ve un instante de texto blanco sin fondo.
                Cuando el fade de salida termina, `auraMounted` pasa a false y
                el bloque se desmonta — que es lo que corta las animaciones. */}
            <div
              className={`relative w-[420px] h-[420px] md:w-[920px] md:h-[700px] transition-opacity duration-700 ${auraDelayClass} ${
                animated && auraVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div
                className="absolute rounded-full blur-3xl animate-drift-1 opacity-85 md:opacity-50"
                style={{
                  width: '60%',
                  height: '55%',
                  backgroundColor: '#1644EB',
                  left: '5%',
                  top: '5%',
                  animationDuration: 'var(--drift-1-dur)',
                }}
              />
              <div
                className="absolute rounded-full blur-3xl animate-drift-2 opacity-80 md:opacity-45"
                style={{
                  width: '55%',
                  height: '55%',
                  backgroundColor: '#3B82F6',
                  right: '5%',
                  top: '15%',
                  animationDuration: 'var(--drift-2-dur)',
                }}
              />
              <div
                className="absolute rounded-full blur-3xl animate-drift-3 opacity-70 md:opacity-40"
                style={{
                  width: '50%',
                  height: '50%',
                  backgroundColor: '#06B6D4',
                  left: '25%',
                  bottom: '5%',
                  animationDuration: 'var(--drift-3-dur)',
                }}
              />
            </div>
          </div>
          )}

          {/* Helper JSX: input container compartido entre empty y active.
              No es un sub-componente — vive acá adentro para tener acceso
              a `prompt`, `attachOpen`, refs y handlers sin prop drilling. */}
          {(() => {
            // Fila de chips de adjuntos (imagen / producto / modelo) que el
            // user enganchó desde el menú "+". Se muestra arriba del textarea
            // y se manda como contexto con el próximo mensaje.
            const hasChips = chat.attachments.length > 0 || imageUploading;
            const chipsRow = hasChips ? (
              <div className="flex flex-wrap items-center gap-2 px-3 pt-3">
                {chat.attachments.map((a) => {
                  const thumb =
                    a.kind === 'image'
                      ? a.previewUrl
                      : a.imageUrl;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setPreviewAttachment(a)}
                      className="group relative w-16 h-16 rounded-xl overflow-hidden bg-neutral-100 border border-black/[0.06] cursor-pointer"
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={a.kind === 'image' ? 'Imagen' : a.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="w-full h-full inline-flex items-center justify-center text-brand-primary">
                          {a.kind === 'model' ? (
                            <User className="w-6 h-6" />
                          ) : (
                            <ImageIcon className="w-6 h-6" />
                          )}
                        </span>
                      )}
                      {/* Botón cerrar dentro de la imagen, esquina superior
                          derecha. Mobile: siempre visible. Desktop: al hover. */}
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => {
                          e.stopPropagation();
                          chat.removeAttachment(a.id);
                        }}
                        aria-label="Quitar adjunto"
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-black/55 hover:bg-black/75 text-white shadow-sm transition-opacity opacity-100 md:opacity-0 md:group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                    </button>
                  );
                })}
                {imageUploading && (
                  <div className="w-16 h-16 rounded-xl bg-neutral-100 border border-black/[0.06] inline-flex items-center justify-center">
                    <span className="w-4 h-4 rounded-full border-2 border-brand-primary/30 border-t-brand-primary animate-spin" />
                  </div>
                )}
              </div>
            ) : null;

            const inputBox = (
              <div className="w-full max-w-[680px] bg-white border border-black/[0.06] rounded-2xl shadow-sm ring-1 ring-brand-primary/15">
                {/* En MOBILE + empty state los adjuntos ya se ven en la card
                    de referencias, así que acá se ocultan para no mostrarlos
                    dos veces. En desktop y en el estado activo (donde la card
                    no existe) los chips siguen siendo el único lugar. */}
                {chipsRow && (
                  <div className={hasMessages ? '' : 'hidden md:block'}>
                    {chipsRow}
                  </div>
                )}
                <div className="px-5 pt-4">
                  <textarea
                    ref={promptRef}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                      // Enter (sin Shift) envía. Shift+Enter = salto de línea.
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleGenerate();
                      }
                    }}
                    placeholder={
                      hasMessages
                        ? 'Pedile algo a Atlas…'
                        : 'Describí lo que querés generar…'
                    }
                    rows={1}
                    disabled={chat.streaming}
                    // `min-h` de dos líneas en MOBILE: la caja arranca con
                    // aire para escribir en vez de una sola línea apretada.
                    // Va como min-height y no como `rows` porque el autosize
                    // escribe `style.height` inline — min-height igual gana.
                    // 15.5px × leading-snug (1.375) ≈ 21.3px por línea.
                    className="w-full resize-none bg-transparent border-none outline-none text-[15.5px] text-brand-black/90 placeholder:text-[#3C3C43]/35 leading-snug overflow-hidden disabled:cursor-not-allowed min-h-[43px] md:min-h-0"
                  />
                </div>
                {/* Row inferior: [⚙] ([+] solo chateando con IA) … [Ayuda de IA]
                    [créditos] [↑]. El posicionamiento por breakpoint se logra
                    con los `ml-auto`.
                    OJO: NADA de `overflow-x-auto` acá. Esta fila contiene los
                    triggers de TRES popovers (⚙, +, Ayuda de IA) y overflow en
                    cualquier eje crea contexto de clipping: los menús se
                    recortan y parecen "no funcionar". Si la fila no entra, hay
                    que sacar controles, no scrollearla. */}
                <div className="flex items-center gap-2 px-3 pb-3 pt-2">
                  {/* Ruedita de ajustes de la generación (formato + cantidad
                      o duración). Se ESCONDE una vez que el agente está
                      conversando: ahí el formato, la cantidad y la duración
                      las decide él según lo que le pidas, así que dejarlas
                      visibles prometía un control que no manda. */}
                  {!agenteConversando && (
                    <ConfiguracionMenu
                      outputKind={outputKind}
                      onChangeOutputKind={cambiarOutputKind}
                      formatSlug={formatSlug}
                      onChangeFormat={setFormatSlug}
                      cantidad={cantidad}
                      onChangeCantidad={setCantidad}
                      durationSeconds={videoDuration}
                      onChangeDuration={setVideoDuration}
                      open={configMenuOpen}
                      onOpenChange={setConfigMenuOpen}
                    />
                  )}
                  {/* Opciones del chat (Nuevo chat / Consumo / Memoria /
                      Historial). Antes vivían en una hamburguesa flotante
                      arriba a la derecha; ahora viven acá, y SOLO con Ayuda
                      de IA: son todas nociones del chat (historial de chats,
                      memoria del agente, créditos de la conversación) que en
                      modo directo no significan nada. */}
                  {aiHelp && (
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        aria-label="Opciones del chat"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" strokeWidth={2.25} />
                      </button>
                      {menuOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-[55]"
                            onClick={() => setMenuOpen(false)}
                          />
                          {/* Abre hacia ARRIBA: la fila vive al pie del sheet. */}
                          <div className="absolute bottom-full left-0 mb-2 w-[250px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56]">
                            {menuContent}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {/* Adjuntar "+": SOLO cuando se está chateando con Ayuda de
                      IA. En modo directo los adjuntos entran por las cards del
                      empty state, y acá el botón sería ruido. */}
                  {aiHelp && hasMessages && (
                    <div className="relative shrink-0" data-attach-menu>
                      <button
                        type="button"
                        onClick={() => setAttachOpen((v) => !v)}
                        aria-label="Adjuntar"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3] transition-colors"
                      >
                        <Plus className="w-4 h-4" strokeWidth={2.25} />
                      </button>
                      {/* Popup desktop — anclado al botón. Solo md+. El
                          contenido (root / fuentes) lo arma `attachMenuContent`
                          afuera del closure así se comparte con el bottom
                          sheet mobile. */}
                      {attachOpen && (
                        <div className="hidden md:block absolute bottom-full left-0 mb-2 w-[280px] bg-white border border-black/[0.06] rounded-2xl shadow-xl p-2 z-[56]">
                          {attachMenuContent}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="md:ml-auto">
                    <AyudaIAMenu
                      aiHelp={aiHelp}
                      onToggleAiHelp={handleToggleAiHelp}
                      intelligence={intelligence}
                      onChangeIntelligence={setIntelligence}
                      open={aiMenuOpen}
                      onOpenChange={setAiMenuOpen}
                    />
                  </div>
                  {chat.streaming ? (
                    // Turno en curso → botón Detener (escape hatch si el
                    // stream se colgó; también corta un turno largo a
                    // propósito).
                    <button
                      type="button"
                      onClick={chat.stopTurn}
                      aria-label="Detener"
                      // Fondo SÓLIDO (no `bg-brand-black/85`): brand-black es
                      // un `var(--brand-black)` con hex → el modificador de
                      // opacidad genera CSS inválido y el fondo NO se aplica
                      // (el botón quedaba invisible salvo en hover). Ver ADR-140.
                      className="relative shrink-0 ml-auto md:ml-0 inline-flex items-center justify-center w-9 h-9 rounded-full shadow-sm bg-brand-black hover:opacity-90 text-white transition-opacity duration-200"
                    >
                      <Square className="w-3 h-3 fill-current" />
                    </button>
                  ) : (
                    <>
                      {/* Costo en créditos — SOLO en modo directo, donde el
                          precio es determinista (sabemos exactamente qué se
                          va a generar). Con Ayuda de IA el agente puede hacer
                          cualquier cosa, así que anunciar un número sería
                          mentir. Aparece recién cuando hay texto: sin prompt
                          no hay nada que cobrar. */}
                      {/* Pill de créditos y flecha de enviar: en MOBILE solo
                          existen cuando el agente ya está conversando. En el
                          resto de los casos hay un botón "Generar" de ancho
                          completo debajo de la caja, que ya lleva el costo
                          adentro — tener las dos cosas duplicaba la acción y el
                          número. En desktop van siempre (no hay botón grande). */}
                      {!aiHelp && hasText && (
                        <span
                          className={`shrink-0 ml-auto md:ml-0 items-center h-7 px-2.5 rounded-full bg-neutral-100 text-[12.5px] font-medium text-[#02062BB3] whitespace-nowrap tabular-nums ${
                            agenteConversando ? 'inline-flex' : 'hidden md:inline-flex'
                          }`}
                        >
                          {directCreditCost}{' '}
                          {directCreditCost === 1 ? 'crédito' : 'créditos'}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={
                          hasText && !sendDisabled ? handleGenerate : undefined
                        }
                        disabled={sendDisabled}
                        aria-label="Enviar al asistente"
                        className={`relative shrink-0 ${
                          !aiHelp && hasText ? '' : 'ml-auto md:ml-0'
                        } items-center justify-center w-9 h-9 rounded-full shadow-sm transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                          agenteConversando ? 'inline-flex' : 'hidden md:inline-flex'
                        } ${
                            hasText
                              ? 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                              : 'bg-neutral-100 hover:bg-neutral-200 text-[#02062BB3]'
                        }`}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );

            // Cards de acción del empty state — reemplazan a las viejas
            // pills de sugerencia de prompt. Los tres handlers son los MISMOS
            // que ya usa el menú "+" (ADR-2026-08-19).
            const accionCards = (
              <AccionCardsRow
                onAddProduct={() => setAttachPicker('product')}
                onAddModel={() => setAttachPicker('model')}
                onUploadImage={() => fileInputRef.current?.click()}
                onOpenReferencias={() => setReferenciasOpen(true)}
                referencias={chat.attachments.map((a) => ({
                  id: a.id,
                  url:
                    a.kind === 'image'
                      ? a.previewUrl
                      : a.kind === 'product'
                        ? a.imageUrl
                        : a.imageUrl,
                  label: a.kind === 'image' ? 'Imagen' : a.name,
                }))}
                onRemoveReferencia={chat.removeAttachment}
                uploading={imageUploading}
              />
            );

            // Botón Generar de ancho completo — SOLO mobile, y solo cuando la
            // flecha del composer NO está (o sea, salvo con el agente ya
            // conversando). Es el mismo botón que cierra los demás modos
            // (ConfigurePanel), así que llegar acá desde otro modo no cambia
            // de lenguaje. En desktop nunca aparece: ahí manda la flecha.
            const generarButton = agenteConversando ? null : (
              <button
                type="button"
                onClick={hasText && !sendDisabled ? handleGenerate : undefined}
                disabled={sendDisabled || !hasText}
                className="md:hidden w-full max-w-[680px] inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-5 h-5" />
                <span>Generar</span>
                {/* Costo solo en modo directo: con Ayuda de IA el agente puede
                    hacer cualquier cosa y anunciar un número sería mentir. */}
                {!aiHelp && (
                  <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold tabular-nums">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    {directCreditCost}
                  </span>
                )}
              </button>
            );

            // Las propuestas de generación ya se renderizan inline en el
            // stream (MessageRow con showPendingCards), así que el slot solo
            // se queda con los pickers — que dependen de su scroll y su
            // sticky para funcionar.
            const slotCard =
              chat.activeToolCard && !isInlinePendingCard(chat.activeToolCard)
                ? chat.activeToolCard
                : null;
            const toolCardSlot = slotCard && (
              <div
                key={slotCard.id}
                className="w-full max-w-[680px] animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]"
                style={{ maxHeight: '65dvh' }}
              >
                <ToolCardRenderer
                  card={slotCard}
                  chat={chat}
                  embedded
                />
              </div>
            );

            if (!hasMessages) {
              // ── Empty state ──────────────────────────────────────────
              // Desktop: bloque centrado (título + input + atajos) + el
              // "continuar chat" va al footer de abajo.
              // Mobile: el título se centra arriba y el cluster queda
              // anclado al pie en orden atajos → continuar → input (input
              // abajo del todo). Logrado con `order-*` por breakpoint sin
              // duplicar el input (su ref es única).
              return (
                <div
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto flex flex-col px-5 md:px-8 md:items-center md:justify-center md:py-6"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  {/* Toggle Imagen|Video — MOBILE, arriba del título. En
                      desktop vive pegado al input (ver más abajo), donde el
                      bloque entero está centrado verticalmente. */}
                  <div className="order-0 md:hidden w-full flex justify-center pt-3 pb-6">
                    <OutputKindToggle
                      value={outputKind}
                      onChange={cambiarOutputKind}
                    />
                  </div>
                  {/* Título — mobile: ocupa el espacio de arriba y se centra;
                      desktop: parte del bloque centrado. Al enfocar el input
                      en mobile (teclado abierto) se desvanece sutilmente
                      (en desktop siempre visible). */}
                  <div
                    // En desktop el título respira más: el bloque de abajo
                    // arranca con el toggle Imagen/Video, y con mb-10 le
                    // quedaba encima. En mobile no aplica (el título ocupa el
                    // espacio de arriba con flex-1 y el toggle vive aparte).
                    className={`order-1 flex-1 md:flex-none flex items-center justify-center md:mb-20 transition-opacity duration-300 md:opacity-100 ${
                      inputFocused ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    {/* Con Ayuda de IA el título va blanco sobre el aura. En
                        modo directo NO hay aura y el fondo del sheet es claro:
                        el blanco quedaría invisible, así que va en
                        brand-black (ADR-2026-08-19).
                        El color transiciona en 700ms, igual que el fade del
                        aura, para que el cambio de modo sea un solo gesto
                        continuo y no dos cortes separados. */}
                    {/* Dos líneas: "Crea" en la fuente de la app (font-sans,
                        Plus Jakarta) y "sin límites" en la display serif en
                        itálica. El contraste entre las dos es intencional —
                        antes iba todo en Playfair. */}
                    <h1
                      className={`text-[32px] md:text-[44px] font-normal text-center leading-tight transition-colors duration-700 ${
                        aiHelp
                          ? 'text-white drop-shadow-md'
                          : 'text-brand-black'
                      }`}
                    >
                      <span className="block font-sans font-semibold">
                        Creá
                      </span>
                      <span className="block font-display italic">
                        sin límites
                      </span>
                    </h1>
                  </div>
                  {/* Input — mobile abajo del todo (order-4); desktop justo
                      bajo el título (order-2). */}
                  <div className="order-4 md:order-2 w-full max-w-3xl mx-auto flex flex-col items-center mt-5 md:mt-0">
                    {/* Toggle Imagen|Video — SOLO desktop: en mobile vive
                        arriba de todo, en el chrome del Sidebar (y se repite
                        dentro del menú de la ruedita como red de seguridad).
                        Va arriba del aviso de créditos, que está diseñado
                        como extensión pegada al input (`-mb-3`) y no admite
                        nada entre medio. */}
                    <div className="hidden md:flex mb-3 justify-center">
                      <OutputKindToggle
                        value={outputKind}
                        onChange={cambiarOutputKind}
                      />
                    </div>
                    {/* Aviso de consumo — extensión gris pegada ARRIBA del
                        input, con una x para dejar de mostrarlo (persistido en
                        localStorage). SOLO con Ayuda de IA: en modo directo no
                        hay costo de razonamiento y el texto sería falso. */}
                    {aiHelp && !creditsDismissed && (
                      <div className="w-full max-w-[680px] flex items-center gap-1.5 rounded-t-2xl bg-[#F2F2F7] pl-4 pr-1.5 pt-2 pb-4 -mb-3">
                        <p className="flex-1 text-[11.5px] text-brand-black/55 leading-snug">
                          El asistente consumirá créditos basados en su uso.
                        </p>
                        <button
                          type="button"
                          onClick={dismissCreditsNotice}
                          aria-label="No mostrar de nuevo"
                          className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full text-brand-black/40 hover:text-brand-black/70 hover:bg-black/[0.06] transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <div className="relative z-10 w-full flex justify-center">
                      {inputBox}
                    </div>
                    {/* Generar full-width — mobile, debajo del container del
                        prompt, como en los demás modos. */}
                    <div className="w-full flex justify-center mt-3">
                      {generarButton}
                    </div>
                  </div>
                  {/* Cards de acción — visibles SIEMPRE en el empty state, en
                      ambos breakpoints. A diferencia de las viejas pills (que
                      en mobile aparecían recién al enfocar el input), las
                      cards son la entrada principal al modo: tienen que verse
                      al llegar. Mobile: arriba del input (order-3, el input es
                      order-4). Desktop: debajo (md:order-3, input md:order-2). */}
                  {/* Block en mobile, flex centrado en desktop. El carrusel
                      de referencias necesita un padre BLOCK para poder salirse
                      a los bordes con `-mx-5`: dentro de un `flex
                      justify-center` el margen negativo se compensa solo
                      (achica la caja de margen, el centrado reparte esos 40px
                      y el borde queda donde estaba). */}
                  <div className="order-3 w-full max-w-3xl mx-auto md:flex md:justify-center mt-4 md:mt-8">
                    {accionCards}
                  </div>
                </div>
              );
            }

            // ── Active state ───────────────────────────────────────────
            // Mensajes scrolleables arriba, input (o tool card) anclado
            // al pie con safe-area-inset.
            return (
              <>
                <div
                  ref={messagesScrollRef}
                  onScroll={handleMessagesScroll}
                  onWheel={releasePinOnUserScroll}
                  onTouchMove={releasePinOnUserScroll}
                  className="relative z-10 flex-1 min-h-0 overflow-y-auto px-5 md:px-8"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <div className="max-w-3xl mx-auto flex flex-col gap-4 pt-6 pb-28">
                    {chat.messages.map((msg, i) => (
                      <MessageRow
                        key={i}
                        msg={msg}
                        chat={chat}
                        showPendingCards
                        userRef={i === lastUserIdx ? latestUserMsgRef : undefined}
                      />
                    ))}
                    {(() => {
                      // Indicador de actividad. Si hay una tool server-side en
                      // uso (web search / scraper), mostramos su label real.
                      // Sino, "Pensando…" mientras el agente prepara la
                      // respuesta/card y no hay nada visible todavía.
                      if (chat.streaming && chat.toolActivity) {
                        return <ThinkingIndicator label={chat.toolActivity} />;
                      }
                      const last = chat.messages[chat.messages.length - 1];
                      const preparing =
                        chat.streaming &&
                        !chat.activeToolCard &&
                        (!last ||
                          last.role === 'user' ||
                          (last.role === 'assistant' &&
                            !last.text &&
                            (!last.toolCards || last.toolCards.length === 0)));
                      return preparing ? <ThinkingIndicator /> : null;
                    })()}
                  </div>
                </div>
                <div
                  className="relative z-10 shrink-0 px-5 md:px-8 pt-3"
                  style={{
                    paddingBottom:
                      'calc(env(safe-area-inset-bottom, 0px) + 1.5rem)',
                  }}
                >
                  <div className="max-w-3xl mx-auto flex flex-col items-center">
                    {/* El toggle viaja con el composer: en el estado activo
                        también tiene que estar, porque decide qué genera el
                        PRÓXIMO prompt. Acá va en AMBOS breakpoints — en el
                        empty state mobile vive arriba del título, pero en el
                        activo no hay título, así que este es su lugar. Se
                        oculta mientras una tool card ocupa el slot: ahí no
                        hay caja de prompt a la que aplicarle el toggle. Y
                        tampoco va si el agente ya está conversando: ahí el
                        tipo de salida lo decide él. */}
                    {!toolCardSlot && !agenteConversando && (
                      <div className="flex mb-3 justify-center">
                        <OutputKindToggle
                          value={outputKind}
                          onChange={cambiarOutputKind}
                        />
                      </div>
                    )}
                    {toolCardSlot ?? inputBox}
                    {/* Generar full-width también en el estado activo: en modo
                        directo la flecha del composer no existe en mobile, así
                        que sin esto no habría con qué enviar el segundo
                        prompt. No va si una tool card ocupa el slot. */}
                    {!toolCardSlot && generarButton && (
                      <div className="w-full flex justify-center mt-3">
                        {generarButton}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Desktop: continuar chat anterior, abajo del todo (solo empty /
              nuevo chat). El aviso de consumo ahora vive arriba del input
              (extensión gris) en ambos breakpoints. */}
          {!hasMessages && continueButton && (
            <div className="hidden md:block relative z-10 shrink-0 px-8 pb-6 pt-2">
              <div className="max-w-3xl mx-auto flex flex-col items-center gap-2">
                {continueButton}
              </div>
            </div>
          )}
        </div>

        {/* ── Historial de chats (slide-in) ── */}
        {/* Portaleado a document.body: el sheet del asistente tiene un
            `transform` (animación) → un `fixed inset-0` anidado se ancla a ESE
            contenedor (offset por la sidebar) y no cubre la sidebar. El portal
            lo devuelve al viewport real. z-[63] > z-30 de la sidebar. */}
        {historialOpen &&
          ReactDOM.createPortal(
            <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setHistorialOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="flex items-center justify-between px-4 pb-2 shrink-0">
                <h3 className="text-[15px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <button
                  type="button"
                  onClick={() => setHistorialOpen(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {historialListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 shrink-0">
                <h3 className="text-[17px] font-semibold text-brand-black/90">
                  Historial de chats
                </h3>
                <button
                  type="button"
                  onClick={() => setHistorialOpen(false)}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {historialListContent}
              </div>
            </div>
            </>,
            document.body,
          )}

        {/* ── Memoria del asistente (slide-in, mismo patrón que historial) ── */}
        {memoriaOpen &&
          ReactDOM.createPortal(
            <>
            <div
              className="fixed inset-0 z-[63] bg-black/40 animate-fade-in"
              onClick={() => setMemoriaOpen(false)}
            />
            {/* Mobile: bottom sheet */}
            <div
              className="md:hidden fixed inset-x-0 bottom-0 z-[64] bg-white rounded-t-[20px] shadow-2xl flex flex-col animate-slide-up-sheet"
              style={{
                maxHeight: '80dvh',
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="py-2 shrink-0">
                <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
              </div>
              <div className="px-4 pb-1 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[15px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[12.5px] text-[#3C3C43]/55 leading-snug pb-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="flex-1 overflow-y-auto px-3 pb-4">
                {memoriaListContent}
              </div>
            </div>
            {/* Desktop: side panel */}
            <div className="hidden md:flex fixed top-4 right-4 bottom-4 w-[420px] z-[64] bg-white rounded-[13px] shadow-2xl flex-col overflow-hidden">
              <div className="px-5 pt-4 pb-3 shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-[17px] font-semibold text-brand-black/90">
                    Memoria del asistente
                  </h3>
                  <button
                    type="button"
                    onClick={() => setMemoriaOpen(false)}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[13px] text-[#3C3C43]/55 leading-snug mt-1">
                  Lo que el asistente recuerda de vos y tu marca entre chats.
                </p>
              </div>
              <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
              <div className="flex-1 overflow-y-auto px-3 py-3">
                {memoriaListContent}
              </div>
            </div>
            </>,
            document.body,
          )}


        {/* Mobile bottom sheet del menú "+". Solo se monta cuando
            attachOpen=true y estamos en mobile. */}
        {attachOpen && (
          <div className="md:hidden fixed inset-0 z-[62]">
            <div
              className="absolute inset-0 bg-black/40 animate-fade-in"
              onClick={() => setAttachOpen(false)}
            />
            <div
              data-attach-menu
              className="absolute inset-x-0 bottom-0 bg-white rounded-t-[20px] shadow-2xl p-3 animate-slide-up-sheet"
              style={{
                paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              }}
            >
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto mb-3" />
              {attachMenuContent}
            </div>
          </div>
        )}
      </div>

      {/* Input de archivo oculto — "Añadir imagen" del menú "+". */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          onPickImageFile(f);
          // Permitir re-elegir el mismo archivo.
          e.target.value = '';
        }}
      />

      {/* Picker real de productos (mismo que el agente / modo modelo). */}
      {attachPicker === 'product' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={() => setAttachPicker('none')}
          onAccept={onAcceptProducts}
        />
      )}

      {/* Picker de imágenes ya generadas. */}
      <GeneratedImagesPicker
        open={attachPicker === 'generated'}
        companyId={companyId}
        onCancel={() => setAttachPicker('none')}
        onAccept={onAcceptGenerated}
      />

      {/* Picker real de modelos. */}
      <ModelPickerSheet
        open={attachPicker === 'model'}
        selection={null}
        onChange={onPickModel}
        onClose={() => setAttachPicker('none')}
        userModels={userModels}
        userId={userId}
        companyId={companyId}
        onModelCreated={refreshModels}
        hideAuto
      />

      {/* Pantalla de carga tradicional — SOLO modo directo. Mismo componente
          y misma duración (80s) que usan los demás modos para imagen, así la
          espera se siente igual en toda la app. Al completarse salta a las
          imágenes generadas. */}
      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={80_000}
          completeNow={completeProgressNow}
          onCompleted={() => {
            setShowLoadingScreen(false);
            setCompleteProgressNow(false);
            const pend = pendingNavRef.current;
            pendingNavRef.current = null;
            if (pend) {
              navigate('/redes-sociales/imagenes-generadas', {
                state: { imageIds: pend.imageIds, companyId: pend.companyId },
              });
            }
          }}
        />
      )}

      {/* Sheet único de referencias — MOBILE. Reemplaza a las 3 cards
          sueltas: una sola puerta con pestañas Subidas/Productos/Modelos. */}
      <ReferenciasSheet
        open={referenciasOpen}
        onClose={() => setReferenciasOpen(false)}
        attachments={chat.attachments}
        onRemoveAttachment={chat.removeAttachment}
        models={userModels}
        onUploadImage={() => fileInputRef.current?.click()}
        // Commit único de "Utilizar". La selección de productos que llega es
        // la COMPLETA, así que sincronizamos en los dos sentidos: sacamos los
        // chips que el user deseleccionó y agregamos los nuevos. Sin esto,
        // deseleccionar en el panel no quitaba el chip.
        onUtilizar={({ products, model }) => {
          const nuevos = new Set(products.map((p) => p.id));
          for (const att of chat.attachments) {
            if (att.kind === 'product' && !nuevos.has(att.productId)) {
              chat.removeAttachment(att.id);
            }
          }
          const yaAdjuntos = new Set(
            chat.attachments
              .filter(
                (a): a is Extract<ChatAttachment, { kind: 'product' }> =>
                  a.kind === 'product',
              )
              .map((a) => a.productId),
          );
          onAcceptProducts(
            products.map((p) => p.id).filter((id) => !yaAdjuntos.has(id)),
          );
          // `null` = no se tocó el modelo, así que no lo pisamos.
          if (model) onPickModel(model);
        }}
        uploading={imageUploading}
      />

      {/* Lightbox de adjunto — imagen ampliada con fondo desenfocado. Si es
          producto o modelo, el nombre aparece sutil debajo. */}
      {previewAttachment && (
        <div
          className="fixed inset-0 z-[300] flex flex-col items-center justify-center px-6 bg-black/55 backdrop-blur-md animate-fade-in"
          onClick={() => setPreviewAttachment(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewAttachment(null)}
            aria-label="Cerrar"
            className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white backdrop-blur-sm transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="flex flex-col items-center gap-4 max-w-[88vw]"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const src =
                previewAttachment.kind === 'image'
                  ? previewAttachment.previewUrl
                  : previewAttachment.imageUrl;
              return src ? (
                <img
                  src={src}
                  alt={
                    previewAttachment.kind === 'image'
                      ? 'Imagen'
                      : previewAttachment.name
                  }
                  className="max-w-full max-h-[72vh] rounded-2xl object-contain shadow-2xl"
                />
              ) : (
                <span className="inline-flex items-center justify-center w-40 h-40 rounded-2xl bg-white/10 text-white">
                  {previewAttachment.kind === 'model' ? (
                    <User className="w-16 h-16" />
                  ) : (
                    <ImageIcon className="w-16 h-16" />
                  )}
                </span>
              );
            })()}
            {previewAttachment.kind !== 'image' && (
              <span className="text-white/80 text-[15px] font-medium text-center">
                {previewAttachment.name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Candado de las dos opciones pagas del modo —
          ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
          Se reusa `FeatureLockedModal` (el mismo que veía este modo cuando estaba
          bloqueado entero) en vez de inventar un cartel nuevo: es el lenguaje de
          upsell que la app ya tiene, y también se portalea a `document.body`. */}
      <FeatureLockedModal
        open={!!lockOpcion}
        onClose={() => setLockOpcion(null)}
        title={lockOpcion?.title ?? ''}
        message={lockOpcion?.message ?? ''}
        ctaLabel="Mejorar plan"
      />
    </>,
    document.body,
  );
};

/** Item del menú de adjuntos del Asistente de marketing — fila full-width
 *  con icon + label + chevron. Usado tanto en el popup desktop como en el
 *  bottom sheet mobile. */
const AttachMenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ icon, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 text-left transition-colors"
  >
    <span className="inline-flex items-center gap-3 text-[14.5px] text-brand-black/90">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-primary/10 text-brand-primary shrink-0">
        {icon}
      </span>
      {label}
    </span>
    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
  </button>
);
